'use client';

/**
 * BrandedLoadingScreen — The single, canonical full-screen loading state
 * for all auth transitions and app-level gates.
 *
 * Design: clean dark background, centered VirzaMark with a subtle breathing
 * glow-pulse animation. Enterprise-grade minimalism — no orbs, no noise.
 *
 * Usage sites:
 * - login-form.tsx success state
 * - auth/callback/page.tsx (PKCE exchange / sync)
 * - AuthTransitionOverlay (callback → dashboard bridge)
 * - ClientShell gate (unauthenticated / onboarding redirect)
 */

import { VirzaMark } from '@/components/branding/virza-mark';
import { cn } from '@/lib/utils';

interface BrandedLoadingScreenProps {
  /** Contextual subtitle shown below the logo. */
  message?: string;
  /** Additional CSS classes on the outer container. */
  className?: string;
}

/**
 * Renders as a plain div (not fixed/absolute) — the consumer controls
 * positioning (e.g. `fixed inset-0`, `min-h-screen`, `h-dvh`).
 */
export function BrandedLoadingScreen({ message, className }: BrandedLoadingScreenProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'bg-background text-foreground flex flex-col items-center justify-center',
        className,
      )}
    >
      <div className="animate-glow-pulse" aria-hidden="true">
        <VirzaMark variant="auto" size={96} />
      </div>
      {message && <p className="text-muted-foreground mt-6 text-sm">{message}</p>}
      {!message && <span className="sr-only">Loading</span>}
    </div>
  );
}
